<script lang="ts" setup>
import { AlertCircleIcon, CheckCircleIcon, PencilIcon } from 'mdi-vue3'
import { computed, type HTMLAttributes, markRaw, ref, toRefs, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'

import { hasSlot } from '../../../main'
import AButtons, { type AButtonsItemInterface } from '../AButton/AButtons.vue'
import { CardDescription, CardHeader, CardTitle } from '../card'

defineOptions({ inheritAttrs: true })

const { t } = useI18n({
  messages: {
    en: {
      edit: 'Edit',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving',
    },
    nl: {
      edit: 'Aanpassen',
      cancel: 'Annuleren',
      save: 'Opslaan',
      saving: 'Opslaan',
    },
  },
})

export type ACardSubmitEvent = {
  setState: (state: ACardState) => void
}

export type ACardState = 'default' | 'edit' | 'success' | 'error' | 'loading'

export interface ACardFooterInterface extends AButtonsItemInterface {
  position?: 'left' | 'right'
}

// Props
const properties = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    title?: string
    description?: string
    actions?: AButtonsItemInterface[]
    footer?: ACardFooterInterface[]
    collapse?: boolean
    contentCollapse?: boolean
    titleCollapse?: boolean
    absoluteTitle?: boolean
    editable?: boolean
    border?: boolean
  }>(),
  {
    title: '',
    class: undefined,
    description: '',
    collapse: false,
    contentCollapse: false,
    titleCollapse: false,
    absoluteTitle: false,
    editable: false,
    isDialogCard: false,
    actions: () => [],
    footer: () => [],
    border: true,
  },
)

// end Props

const slots = useSlots()
const { actions, footer, editable: isEditable } = toRefs(properties)

// Emits
const emit = defineEmits<{
  (event: 'submit', $event: ACardSubmitEvent): void
  (event: 'cancel'): void
  (event: 'close'): void
}>()
// end Emits

// Refs
const state = ref<ACardState>('default')

const timeoutId = ref<ReturnType<typeof setTimeout> | undefined>(undefined)

// end Refs

// Computed
const classes = computed(() => ({
  'atabix-card': true,
  [`atabix-card--padding`]: !properties.collapse,
  [`atabix-card--border`]: properties.border,
}))

const hasTitle = computed(() => !!properties.title || hasSlot('title', slots))

const hasActions = computed(() => internalActions.value.length > 0 || hasSlot('actions', slots))

const hasFooter = computed(() => internalFooter.value.length > 0 || hasSlot('footer', slots))

const internalActions = computed(() => {
  const items = [...actions.value]

  if (state.value === 'default' && isEditable.value) {
    items.push(editAction.value)
  }

  if (state.value === 'error' && isEditable.value) {
    items.push(errorAction.value)
  }

  if (state.value === 'success' && isEditable.value) {
    items.push(successAction.value)
  }

  return items
})

const internalFooter = computed(() => {
  const items = [...footer.value]
  if (['edit', 'loading', 'error'].includes(state.value) && isEditable.value) {
    items.push(cancelAction.value, submitAction.value)
  }

  return items
})

const cancelAction = computed<AButtonsItemInterface>(() => ({
  title: t('cancel'),
  sticky: true,
  variant: 'ghost',
  click: () => {
    setState('default')
    emit('cancel')
  },
}))

const editAction = computed<AButtonsItemInterface>(() => ({
  tooltip: t('edit'),
  icon: markRaw(PencilIcon),
  class: 'text-base',
  variant: 'outline',
  sticky: true,
  click: () => setState('edit'),
}))

const errorAction = computed<AButtonsItemInterface>(() => ({
  icon: markRaw(AlertCircleIcon),
  variant: 'destructive',
  sticky: true,
  click: () => {},
}))

const successAction = computed<AButtonsItemInterface>(() => ({
  icon: markRaw(CheckCircleIcon),
  sticky: true,
  click: () => {},
}))

const submitAction = computed<AButtonsItemInterface>(() => ({
  title: t('save'),
  sticky: true,
  loading: state.value === 'loading',
  disabled: state.value === 'loading',
  variant: 'default',
  click: () => {
    setState('success')
    emit('submit', { setState })
  },
}))
// end Computed

// Functions
function setState(state_: ACardState) {
  state.value = state_
  stopTimeout()

  if (state.value === 'success') {
    startTimeout(() => {
      setState('default')
    }, 1000)
  }

  if (state.value === 'error') {
    startTimeout(() => {
      setState('edit')
    }, 1000)
  }
}

function startTimeout(callback: () => void, ms: number) {
  stopTimeout()
  timeoutId.value = setTimeout(callback, ms)
}

function stopTimeout() {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
  }
}
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <div :class="[classes, properties.class]">
    <CardHeader v-if="hasTitle || hasActions">
      <div
        :class="{
          'atabix-card__title-container': true,
          'atabix-card__title-container--collapse': collapse || titleCollapse,
          'atabix-card__title-container--absolute': absoluteTitle,
        }"
      >
        <template v-if="hasTitle">
          <CardTitle class="atabix-card__title">
            <slot name="title">
              {{ title }}
            </slot>
          </CardTitle>
        </template>
        <CardDescription v-if="description">
          {{ description }}
        </CardDescription>
      </div>

      <div v-if="hasActions" class="atabix-card__actions">
        <slot name="actions">
          <AButtons v-if="internalActions.length > 0" :items="internalActions" />
        </slot>
      </div>
    </CardHeader>

    <div v-if="(hasTitle || hasActions) && !absoluteTitle" class="atabix-card__separator" />

    <div
      :class="{
        'atabix-card__content': true,
        'atabix-card__content--collapse': collapse || contentCollapse,
      }"
    >
      <slot v-if="['default', 'success'].includes(state)" />
      <slot v-if="['edit', 'loading', 'error'].includes(state)" name="editable" />
    </div>

    <div v-if="hasFooter" class="atabix-card__footer">
      <slot name="footer">
        <div v-if="internalFooter.length > 0" class="flex w-full items-center justify-between">
          <div class="flex items-center gap-8">
            <AButtons :items="internalFooter.filter((item) => item.position === 'left')" sticky />
          </div>
          <div class="flex items-center gap-8">
            <AButtons :items="internalFooter.filter((item) => item.position === 'right' || item.position === undefined)" sticky />
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<style lang="scss">
/* stylelint-disable block-no-empty */
.atabix-card {
  @apply rounded-lg;
  @apply bg-background;
  @apply text-foreground;
  @apply shadow-sm;
}

.atabix-card--border {
  @apply rounded-lg;
  @apply border;
}

.atabix-card--collapse {
}

.atabix-card--borderless {
}

.atabix-card--squared {
}

.atabix-card--content-collapse {
  @apply p-0;
}

.atabix-card__title-container {
  @apply w-full;
  @apply h-auto;
  @apply gap-4;
}

.atabix-card__title {
  @apply text-foreground;
  @apply text-xl;
  @apply font-semibold;
}

.atabix-card__content {
  @apply p-6;
}

.atabix-card__title-container--collapse,
.atabix-card__content--collapse {
  @apply p-0;
}

.atabix-card__title-container--absolute {
}

.atabix-card__footer {
  @apply flex;
  @apply justify-end;
  @apply gap-4;
  @apply px-6;
  @apply py-4;
  @apply bg-background-tint;
  @apply rounded-b-lg;
}

.atabix-card__separator {
  @apply w-full;
  @apply h-px;
  @apply bg-border;
}

.atabix-card__actions {
  @apply text-base;
  @apply text-foreground-subtle;
  @apply hover:text-foreground;
}
</style>
